import React, { useState } from "react";
import { Loader } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { createReview } from "../../services/reviewService";

const RATING_LABELS = ["", "Poor", "Fair", "Good", "Great", "Excellent"];

const RECOMMENDATION_OPTIONS = [
  { value: "yes", label: "🟢 Yes, it's ready" },
  { value: "needs_improvement", label: "🟡 Needs a few improvements" },
  { value: "not_yet", label: "🔴 Not yet" },
];

const ReviewModal = ({ isOpen, onClose, draftId, onSuccess }) => {
  const { apiCall } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [recommendation, setRecommendation] = useState("");
  const [thoughts, setThoughts] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const isValid = rating > 0 && recommendation !== "" && thoughts.trim().length >= 20;

  if (!isOpen) return null;

  const resetForm = () => {
    setRating(0);
    setHoverRating(0);
    setRecommendation("");
    setThoughts("");
    setError("");
  };

  const handleClose = () => {
    if (isLoading) return;
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    setError("");

    if (!rating) {
      setError("Please select a rating");
      return;
    }
    if (!recommendation) {
      setError("Please select a recommendation");
      return;
    }
    if (thoughts.trim().length < 20) {
      setError("Your thoughts must be at least 20 characters");
      return;
    }

    setIsLoading(true);

    try {
      const result = await createReview(
        { draftId, rating, recommendation, thoughts: thoughts.trim() },
        apiCall,
      );

      setIsLoading(false);

      if (!result.success) {
        setError(result.error);
        return;
      }

      resetForm();
      onClose();
      if (onSuccess) onSuccess(draftId);
    } catch (err) {
      setError(err.message || "Failed to submit review");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white w-full sm:w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-lg max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="px-6 py-4">
          <h3 className="text-lg font-semibold text-black">
            Review this Draft
          </h3>
        </div>

        {/* Modal Content */}
        <div className="px-6 py-4">
          {/* Overall Rating */}
          <div className="mb-4">
            <p className="text-sm font-semibold text-black mb-2">
              Overall Rating
            </p>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  disabled={isLoading}
                  className="text-2xl leading-none disabled:cursor-not-allowed"
                >
                  {(hoverRating || rating) >= star ? "⭐" : "☆"}
                </button>
              ))}
              {rating > 0 && (
                <span className="text-xs text-gray-500 ml-2">
                  {RATING_LABELS[rating]}
                </span>
              )}
            </div>
          </div>

          {/* Recommendation */}
          <div className="mb-4">
            <p className="text-sm font-semibold text-black mb-2">
              Would you recommend publishing?
            </p>
            <div className="flex flex-col gap-2">
              {RECOMMENDATION_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setRecommendation(option.value)}
                  disabled={isLoading}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors disabled:cursor-not-allowed ${
                    recommendation === option.value
                      ? "border-black bg-gray-100"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Thoughts */}
          <div className="mb-2">
            <p className="text-sm font-semibold text-black mb-2">
              Your Thoughts
            </p>
            <textarea
              value={thoughts}
              onChange={(e) => setThoughts(e.target.value)}
              disabled={isLoading}
              maxLength={500}
              rows={4}
              placeholder="Write your review..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-black resize-none focus:outline-none focus:border-black disabled:cursor-not-allowed"
            />
            <div className="text-xs text-gray-400 mt-1 text-right">
              {thoughts.trim().length}/500 (min 20)
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg mb-2">
              {error}
            </div>
          )}
        </div>

        {/* Modal Footer - Buttons */}
        <div className="border-t border-gray-150 flex flex-col items-center gap-3 px-6 pt-4 pb-4">
          <button
            onClick={handleSubmit}
            disabled={!isValid || isLoading}
            className={`w-full px-4 py-3 rounded-lg bg-[#b0d284] text-black font-semibold
              hover:bg-[#9ec170]
              active:scale-[0.98]
              focus:outline-none focus:ring-2 focus:ring-[#b0d284]/60
              transition-all duration-300
              flex items-center justify-center gap-2
              ${
                !isValid || isLoading
                  ? "opacity-70 cursor-not-allowed hover:bg-[#b0d284] active:scale-100"
                  : ""
              }
            `}
          >
            {isLoading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Review"
            )}
          </button>

          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-black disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;
